import { ReactElement, useMemo, useState } from 'react';
import { CompanyUser, UserNotificationsSettings } from '@/api';
import {
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UISelectInput,
    UISelectValue,
    UISwitch,
    UIText
} from '@/ui';
import { useUpdateCompanyUserAction } from '../actions';
import { useCompaniesTranslation } from '../hooks';
import { isEqual } from 'lodash';

type NotificationsSettingsSectionProps = {
    companyUser: CompanyUser;
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
};

// The three task-reminder notifications, in the order they fire.
const NOTIFICATION_KEYS: (keyof UserNotificationsSettings)[] = [
    'taskTimeout',
    'task5Min',
    'task10Min'
];

// The volume field is a 0-1 float; we expose it as discrete quarters since the
// design system has no slider. A stored value is snapped to the nearest step.
const VOLUME_STEPS = [0.25, 0.5, 0.75, 1] as const;

function nearestVolumeStep(value: number): number {
    return VOLUME_STEPS.reduce((closest, step) =>
        Math.abs(step - value) < Math.abs(closest - value) ? step : closest
    );
}

/**
 * Per task-reminder notification: whether it fires as a critical alert (breaks
 * through silent / Do Not Disturb) and at what volume. The underlying `sound`
 * is preserved untouched — there is no defined list of sounds to pick from.
 */
export function MemberNotificationsSettingsSection(
    props: NotificationsSettingsSectionProps
): ReactElement {
    const { companyUser, canEdit, editDisabledReason } = props;

    const { t } = useCompaniesTranslation();
    const updateCompanyUser = useUpdateCompanyUserAction({
        successMessage: t(
            'components.member_notifications_settings_section.toast_updated'
        )
    });

    const [isEditing, setIsEditing] = useState(false);
    const [notifications, setNotifications] =
        useState<UserNotificationsSettings>(companyUser.notifications);

    const volumeOptions = useMemo<UISelectValue[]>(
        () =>
            VOLUME_STEPS.map((step) => ({
                value: String(step),
                label: `${Math.round(step * 100)}%`
            })),
        []
    );

    const hasChanges = useMemo(
        () => !isEqual(notifications, companyUser.notifications),
        [notifications, companyUser.notifications]
    );

    function updateItem(
        key: keyof UserNotificationsSettings,
        changes: Partial<UserNotificationsSettings['taskTimeout']>
    ) {
        setNotifications((prev) => ({
            ...prev,
            [key]: { ...prev[key], ...changes }
        }));
    }

    function resetForm() {
        setNotifications(companyUser.notifications);
        setIsEditing(false);
    }

    function onSubmit() {
        updateCompanyUser
            .updateCompanyUser(companyUser.id, { notifications })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <UISection>
            <UISectionHeader
                title={t(
                    'components.member_notifications_settings_section.title'
                )}
                rightButtons={
                    isEditing
                        ? []
                        : [
                              {
                                  text: t('shared.labels.edit'),
                                  variant: 'textDefault',
                                  type: 'button',
                                  disabled: !canEdit,
                                  disabledTooltip: editDisabledReason,
                                  onClick: () => setIsEditing(true)
                              }
                          ]
                }
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <UIText type="default2">
                        {t(
                            'components.member_notifications_settings_section.intro'
                        )}
                    </UIText>
                    {NOTIFICATION_KEYS.map((key) => {
                        const item = notifications[key];
                        const volumeOption = volumeOptions.find(
                            (opt) =>
                                opt.value ===
                                String(nearestVolumeStep(item.criticalVolume))
                        );
                        return (
                            <div
                                key={key}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 16
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 1,
                                        flexDirection: 'column',
                                        gap: 4
                                    }}
                                >
                                    <UIText>
                                        {t(
                                            `components.member_notifications_settings_section.${key}_label`
                                        )}
                                    </UIText>
                                    <UIText type="default2">
                                        {t(
                                            `components.member_notifications_settings_section.${key}_description`
                                        )}
                                    </UIText>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 1,
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 16
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flex: 1,
                                            flexDirection: 'column',
                                            gap: 4
                                        }}
                                    >
                                        <UIText type="default2">
                                            {t(
                                                'components.member_notifications_settings_section.critical_label'
                                            )}
                                        </UIText>
                                        {isEditing ? (
                                            <UISwitch
                                                checked={item.critical}
                                                onChange={(checked) =>
                                                    updateItem(key, {
                                                        critical: checked
                                                    })
                                                }
                                                small
                                            />
                                        ) : (
                                            <UIText
                                                medium
                                                type={
                                                    item.critical
                                                        ? 'link'
                                                        : 'error'
                                                }
                                            >
                                                {item.critical
                                                    ? t(
                                                          'components.member_notifications_settings_section.on'
                                                      )
                                                    : t(
                                                          'components.member_notifications_settings_section.off'
                                                      )}
                                            </UIText>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flex: 1,
                                            flexDirection: 'column',
                                            gap: 4
                                        }}
                                    >
                                        <UIText type="default2">
                                            {t(
                                                'components.member_notifications_settings_section.volume_label'
                                            )}
                                        </UIText>
                                        {isEditing ? (
                                            <UISelectInput
                                                value={volumeOption}
                                                options={volumeOptions}
                                                onChange={(option) => {
                                                    if (option) {
                                                        updateItem(key, {
                                                            criticalVolume:
                                                                Number(
                                                                    option.value
                                                                )
                                                        });
                                                    }
                                                }}
                                                disabled={!item.critical}
                                                clearable={false}
                                                style={{ width: '100%' }}
                                            />
                                        ) : (
                                            <UIText medium>
                                                {volumeOption?.label ?? '—'}
                                            </UIText>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </UISectionContent>
            {isEditing && (
                <UISectionFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'button',
                            loading: updateCompanyUser.loading,
                            disabled: updateCompanyUser.loading || !hasChanges,
                            text: updateCompanyUser.loading
                                ? t('shared.labels.saving')
                                : t('shared.labels.save'),
                            onClick: onSubmit
                        },
                        {
                            text: t('shared.labels.cancel'),
                            type: 'button',
                            disabled: updateCompanyUser.loading,
                            onClick: resetForm
                        }
                    ]}
                />
            )}
        </UISection>
    );
}
